import { isWebinarId } from '@core/shared';
import { EmailStatusType, OptionsApi, ProcessedFollowUpParticipantData } from '@core/swagger-client';
import {
    Button,
    Checkbox,
    CompositePageHeader,
    ContainerNoTopSpace,
    ContainerWebinarInner,
    ContentHeader,
    ErrorMessagePage,
    Heading,
    Input,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    Table,
    useCreateColumnHelper
} from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackLink } from '@online-webinar/ui-frontend';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

export const FollowUpProcessedPage = () => {
    const { t } = useTranslation();

    const { webinarId, followUpId } = useParams({ strict: false });
    if (!isWebinarId(followUpId)) return <ErrorMessagePage statusCode={404} />;

    const { data: webinar, error: webinarError, isLoading: webinarIsLoading } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));
    const {
        data: followUp,
        error: followUpError,
        isLoading: followUpIsLoading
    } = useQuery(OptionsApi.followUp.getById({ webinarId: webinarId!, followUpId: followUpId! }));

    const {
        data: processedFollowUp,
        error: processedFollowUpError,
        isLoading: processedFollowUpIsLoading
    } = useQuery(OptionsApi.followUp.getProcessedById({ webinarId: webinarId!, followUpId: followUpId! }));

    const columnHelper = useCreateColumnHelper<ProcessedFollowUpParticipantData>();
    const columns = [
        columnHelper.accessor((row) => row.participant.emailAddress, {
            id: 'emailAddress',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('Account.Label.Email')}</>
        }),
        columnHelper.accessor((row) => row.participantName, {
            id: 'name',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('ProcessedFollowUp.TableHeader.Name')}</>
        }),
        columnHelper.accessor((row) => row.emailStatus.status, {
            id: 'emailStatus',
            cell: (info) => <>{t(`EmailStatus.${EmailStatusType[info.getValue()]}`)}</>,
            header: () => <>{t('ProcessedFollowUp.TableHeader.EmailStatus')}</>
        }),
        columnHelper.accessor((row) => DateTime.fromISO(row.createdAt).toLocaleString(DateTime.DATETIME_MED), {
            id: 'createdAt',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('ProcessedFollowUp.TableHeader.CreatedAt')}</>
        }),
        columnHelper.accessor((row) => row.onlinePercentage, {
            id: 'onlinePercentage',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('ProcessedFollowUp.TableHeader.OnlinePercentage')}</>
        }),
        columnHelper.accessor((row) => row.pollVotes, {
            id: 'pollVotes',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('ProcessedFollowUp.TableHeader.PollVotes')}</>
        }),
        columnHelper.accessor((row) => row.pollPoints, {
            id: 'pollPoints',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('ProcessedFollowUp.TableHeader.PollPoints')}</>
        }),
        columnHelper.accessor((row) => row.examAnswers, {
            id: 'examAnswers',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('ProcessedFollowUp.TableHeader.ExamAnswers')}</>
        }),
        columnHelper.accessor((row) => row.examPoints, {
            id: 'examPoints',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('ProcessedFollowUp.TableHeader.ExamPoints')}</>
        }),
        columnHelper.accessor((row) => row.surveyAnswers, {
            id: 'surveyAnswers',
            cell: (info) => <>{info.getValue()}</>,
            header: () => <>{t('ProcessedFollowUp.TableHeader.SurveyAnswers')}</>
        })
    ];

    const exportExcelAsync = async () => {
        if (!webinar || !followUp || !processedFollowUp) return;

        const xlsx = await import('xlsx');
        const workbook = xlsx.utils.book_new();
        workbook.Props = {
            Application: 'OnlineWebinar.com',
            AppVersion: '1.0.0',
            Author: 'OnlineWebinar.com',
            CreatedDate: new Date(),
            Title: 'Processed follow ups for ' + webinar.title + ' / ' + followUp.followUp.title
        };

        // prettier-ignore
        const summary = xlsx.utils.aoa_to_sheet([
                [workbook.Props.Title],
                [],
                ['Exported on', , , , new Date()],
                [],
                [],
            ['Title', followUp.followUp.title],
            ['Bcc owner', followUp.followUp.bccOwner ? 'Yes' : 'No'],
            ['Minimum online percentage', followUp.followUp.minimumOnlinePercentage],
            ['Minimum poll points', followUp.followUp.minimumPollPoints],
            ['Minimum poll votes', followUp.followUp.minimumPollVotes],
            ['Minimum exam points', followUp.followUp.minimumExamPoints],
            ['Minimum exam answers', followUp.followUp.minimumExamAnswers],
            ['Minimum survey answers', followUp.followUp.minimumSurveyAnswers],
        ]);
        xlsx.utils.book_append_sheet(workbook, summary, 'Follow up');

        // prettier-ignore
        const data: (number | string | undefined)[][] = [
                ['First name', 'Last name', 'Nickname', 'Email address', 'Company', 'Country', 'Online percentage', 'Poll points', 'Poll votes', 'Exam points', 'Exam answers', 'Survey answers', 'Email status', 'Created at'],
            ];

        processedFollowUp.forEach((item) => {
            data.push([
                item.participant.firstName,
                item.participant.lastName,
                item.participant.nickname,
                item.participant.emailAddress,
                item.participant.companyName,
                item.participant.countryCode,
                item.onlinePercentage,
                item.pollPoints,
                item.pollVotes,
                item.examPoints,
                item.examAnswers,
                item.surveyAnswers,
                t(`EmailStatus.${EmailStatusType[item.emailStatus.status]}`),
                DateTime.fromISO(item.createdAt).toLocaleString(DateTime.DATETIME_MED)
            ]);
        });

        const processedSheet = xlsx.utils.aoa_to_sheet(data);
        xlsx.utils.book_append_sheet(workbook, processedSheet, 'Processed');

        xlsx.writeFileXLSX(workbook, 'ProcessedFollowUp.xlsx');
    };

    const exportExcel = () => {
        exportExcelAsync().then(() => {
            // Done...
        });
    };

    const error = webinarError || followUpError || processedFollowUpError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (!webinar || !followUp || !processedFollowUp || webinarIsLoading || followUpIsLoading || processedFollowUpIsLoading) {
        return <LoaderPage></LoaderPage>;
    }

    return (
        <>
            <ContainerNoTopSpace>
                <PageHeader>
                    <PageHeaderContent>
                        <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />
                    </PageHeaderContent>
                </PageHeader>
                <ContainerWebinarInner>
                    <BackLink to="/$webinarId/follow-up" params={{ webinarId }}>
                        {t('Button.Back')}
                    </BackLink>
                    <ContentHeader contentHeader={t('ProcessedFollowUp.PageHeading')} />
                    <div className="max-w-lg">
                        <Input classNames="mb-6" label={t('EditFollowUp.Label.Name')} value={followUp.followUp.title} readOnly />
                        <Checkbox
                            id="bccOwner"
                            label={t('EditFollowUp.Label.BccOwner')}
                            checked={followUp.followUp.bccOwner}
                            readOnly
                            classNames="mb-6"
                        />
                        <Input
                            classNames="mb-6"
                            label={t('EditFollowUp.Label.MinimumOnlinePercentage')}
                            value={followUp.followUp.minimumOnlinePercentage}
                            type="number"
                            readOnly
                        />
                        <Input
                            classNames="mb-6"
                            label={t('EditFollowUp.Label.MinimumPollPoints')}
                            value={followUp.followUp.minimumPollPoints}
                            type="number"
                            readOnly
                        />
                        <Input
                            classNames="mb-6"
                            label={t('EditFollowUp.Label.MinimumPollVotes')}
                            value={followUp.followUp.minimumPollVotes}
                            type="number"
                            readOnly
                        />
                        <Input
                            classNames="mb-6"
                            label={t('EditFollowUp.Label.MinimumExamPoints')}
                            value={followUp.followUp.minimumExamPoints}
                            type="number"
                            readOnly
                        />
                        <Input
                            classNames="mb-6"
                            label={t('EditFollowUp.Label.MinimumExamAnswers')}
                            value={followUp.followUp.minimumExamAnswers}
                            type="number"
                            readOnly
                        />
                        <Input
                            classNames="mb-6"
                            label={t('EditFollowUp.Label.MinimumSurveyAnswers')}
                            value={followUp.followUp.minimumSurveyAnswers}
                            type="number"
                            readOnly
                        />
                    </div>
                    <Heading variant="h2" classNames="mt-10 2xl:mt-12 mb-4 lg:mb-6 2xl:mb-8">
                        {t('ProcessedFollowUp.Heading.SentTo')}
                    </Heading>
                    <Button variant="primary" onClick={exportExcel}>
                        {t('ProcessedFollowUp.Button.Export')}
                    </Button>
                    <div className="table-responsive bg-white rounded-lg mt-4 shadow-md">
                        <Table columns={columns} data={processedFollowUp} isHeader={true} />
                    </div>
                </ContainerWebinarInner>
            </ContainerNoTopSpace>{' '}
        </>
    );
};
